/**
 * ImportPreviewMetricCard — KPI card padronizado (igual ao usado em Departamentos
 * e no preview de listas de contatos).
 *
 * RELEASE:
 *   2026-05-30 v1.1.0 - Aceita `value` como number | string e novo `subValue`
 *                       opcional. Permite reuso em métricas não numéricas
 *                       (ex: "0%", "há 5 min", "Nunca") como na aba de
 *                       estatísticas do modal de webhook de fluxos.
 *   2026-05-17 v1.0.0 - Extraído de ListPreviewDialog para reuso em previews
 *                       de importação de Negócios e Afiliados.
 */
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

export type ImportMetricVariant = 'primary' | 'success' | 'info' | 'purple' | 'danger';

interface ImportPreviewMetricCardProps {
  title: string;
  // Aceita número (formatado pt-BR) ou string (renderizada literalmente)
  value: number | string;
  icon: React.ReactNode;
  variant: ImportMetricVariant;
  // Texto secundário opcional, exibido abaixo do título
  subValue?: string;
}

const VARIANTS: Record<ImportMetricVariant, { cardBg: string; iconColor: string }> = {
  primary: {
    cardBg: 'bg-purple-100 dark:bg-purple-900/30',
    iconColor: 'text-purple-700 dark:text-purple-300',
  },
  success: {
    cardBg: 'bg-lime-100 dark:bg-green-900/30',
    iconColor: 'text-green-700 dark:text-green-300',
  },
  info: {
    cardBg: 'bg-blue-100 dark:bg-blue-900/30',
    iconColor: 'text-blue-700 dark:text-blue-300',
  },
  purple: {
    cardBg: 'bg-cyan-100 dark:bg-cyan-900/30',
    iconColor: 'text-cyan-700 dark:text-cyan-300',
  },
  danger: {
    cardBg: 'bg-red-100 dark:bg-red-900/30',
    iconColor: 'text-red-700 dark:text-red-300',
  },
};

export function ImportPreviewMetricCard({
  title,
  value,
  icon,
  variant,
  subValue,
}: ImportPreviewMetricCardProps) {
  const styles = VARIANTS[variant];

  // Formata o valor: number vira pt-BR; string mantém literal
  const displayValue =
    typeof value === 'number' ? (value ?? 0).toLocaleString('pt-BR') : value;

  return (
    <Card
      className={cn(
        'border-0 shadow-none cursor-default transition-all hover:shadow-md',
        styles.cardBg,
      )}
    >
      <CardContent className="p-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex flex-col min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <div className={cn('shrink-0', styles.iconColor)}>{icon}</div>
              <p className="text-xs font-medium truncate text-gray-900 dark:text-gray-100">
                {title}
              </p>
            </div>
            {subValue && (
              <p className="text-[10px] text-muted-foreground truncate mt-0.5 pl-6">
                {subValue}
              </p>
            )}
          </div>
          <p className="text-xl font-bold leading-tight shrink-0 text-gray-900 dark:text-gray-100">
            {displayValue}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
